// Shared UI design tokens + self-hosted UI font (Barlow Semi Condensed, OFL).
// Injected once; every src/ui/* module consumes these custom properties so
// margins, backgrounds, radii and the type scale stay consistent across the HUD.
let injected = false;

// HUD scale: 1.0 at 1600 px wide, growing to 1.5 on a 4K frame, never below 1.
// Every px size in the UI is calc(base * var(--ui-scale)), so one number keeps
// the whole HUD proportional to the frame instead of shrinking into it.
export function uiScale() {
  return Math.min(1.5, Math.max(1, (window.innerWidth || 1600) / 1600));
}

function applyUiScale() {
  document.documentElement.style.setProperty('--ui-scale', uiScale().toFixed(3));
}

export function injectTheme() {
  if (injected) return;
  injected = true;
  const css = document.createElement('style');
  css.id = 'ui-theme';
  css.textContent = `
    @font-face { font-family:'Barlow Semi Condensed'; font-style:normal; font-weight:400;
      font-display:block; src:url('fonts/barlow-semicondensed-400.woff2') format('woff2'); }
    @font-face { font-family:'Barlow Semi Condensed'; font-style:normal; font-weight:500;
      font-display:block; src:url('fonts/barlow-semicondensed-500.woff2') format('woff2'); }
    @font-face { font-family:'Barlow Semi Condensed'; font-style:normal; font-weight:600;
      font-display:block; src:url('fonts/barlow-semicondensed-600.woff2') format('woff2'); }
    :root {
      --ui-font:'Barlow Semi Condensed','Arial Narrow',sans-serif;
      /* One scale factor drives the whole HUD: 1.0 at 1600 px wide, up to 1.5
         on a 4K frame, never below 1. Every size below is expressed through it,
         so the HUD grows with the frame instead of shrinking into it.
         (Set from JS below — a pure-CSS clamp() cannot mix a number with a
         viewport length, and an invalid token silently kills every calc.) */
      --ui-scale:1;
      --ui-xs:calc(12px * var(--ui-scale));
      --ui-s:calc(14px * var(--ui-scale));
      --ui-m:calc(16px * var(--ui-scale));
      --ui-l:calc(23px * var(--ui-scale));
      --ui-track:.02em;
      /* inventory grid cell — 62 keeps a full worn load (jacket + shirt + pants
         + 5x5 pack) inside the column at 1080p without scrolling */
      --ui-cell:calc(62px * var(--ui-scale));
      --ui-qslot:calc(50px * var(--ui-scale));  /* quickbar slot */
      --hud-margin:calc(18px * var(--ui-scale));
      --hud-bg:rgba(10,10,10,.55);
      --hud-line:rgba(255,255,255,.10);
      --hud-radius:2px;
      --hud-text:#e6e6e6;
      --hud-dim:#9aa0a6;
      /* THE ONE CONTRAST SPLIT. Round 5's whole inventory screen composited to
         a p99 of 139/255: the brightest 1% of a full-screen UI was a midtone,
         because chrome and data shared --hud-text over a half-transparent
         scrim. DayZ splits them hard — near-black panels, near-white numbers
         and names. --ui-hi is reserved for the elements that CARRY
         INFORMATION (quantities, item names, container captions, equipped
         values, the vitals readouts); panel chrome stays dim and never uses
         it. Do not spend this token on borders, captions of captions, or
         decoration: the moment everything is bright, nothing is. */
      --ui-hi:#f4f4f1;
      /* Vitals/condition ramp. Round 4 put a fully-saturated orange droplet in
         the corner of a desaturated night frame and it became the loudest thing
         on screen. The ramp is now bone -> muted gold -> muted amber -> brick:
         colour is reserved for DEGRADED states and never out-shouts the world.
         --hud-bone is the "everything is fine" neutral. */
      --hud-bone:#cfd2c9;
      --hud-warn:#bfb27c;
      --hud-bad:#b8814f;
      --hud-crit:#ab4034;
      --hud-shadow:0 1px 3px rgba(0,0,0,.85);
      /* panel chrome shared by the inventory screen and any future full-screen
         UI: flat dark slab, square corners, hairline border (DayZ) */
      --panel-bg:rgba(9,9,9,.86);
      --panel-cap:rgba(20,20,20,.92);
      --panel-line:rgba(255,255,255,.09);
    }
    /* counters/quantities must not reflow as digits change */
    .tnum { font-variant-numeric:tabular-nums; font-feature-settings:'tnum' 1; }
    .keycap {
      display:inline-block; min-width:18px; height:18px; line-height:18px; padding:0 4px;
      text-align:center; border-radius:3px; box-sizing:border-box;
      background:rgba(255,255,255,.12); border:1px solid rgba(255,255,255,.25);
      font:600 var(--ui-xs)/16px var(--ui-font); letter-spacing:var(--ui-track);
      color:var(--hud-text); vertical-align:-3px; text-shadow:none;
    }
  `;
  document.head.appendChild(css);
  applyUiScale();
  addEventListener('resize', applyUiScale);
  // Eagerly fetch all weights at boot — otherwise 500/600 load lazily on the
  // first inventory open and font-display:block blanks the text for ~3s.
  if (document.fonts?.load) {
    for (const w of [400, 500, 600]) document.fonts.load(`${w} 13px 'Barlow Semi Condensed'`);
  }
}
